import { useEffect, useMemo, useState, type JSX } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, type SubscriptionPlan } from '../api.js';
import { useActiveProfileId } from '../active-profile.js';
import { Icon } from '../components/Icon.js';
import { Topbar } from './AdminLayout.js';
import { fmtAmountPlain, fmtDate } from '../utils/format.js';

export function SubscriptionPlans(): JSX.Element {
  const nav = useNavigate();
  const [items, setItems] = useState<SubscriptionPlan[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [tab, setTab] = useState<'active' | 'inactive'>('active');

  const [activeProfileId] = useActiveProfileId();
  useEffect(() => {
    setItems(null);
    api.listPlans().then(setItems).catch((e: Error) => setErr(e.message));
  }, [activeProfileId]);

  const counts = useMemo(() => {
    if (!items) return { active: 0, inactive: 0 };
    let active = 0;
    let inactive = 0;
    for (const p of items) p.isActive ? active++ : inactive++;
    return { active, inactive };
  }, [items]);

  const filtered = useMemo(() => {
    if (!items) return [];
    const s = q.toLowerCase();
    const wantActive = tab === 'active';
    return items.filter(
      (p) => p.isActive === wantActive && (p.name + p.slug).toLowerCase().includes(s),
    );
  }, [items, q, tab]);

  return (
    <>
      <Topbar
        title="Подписки — планы"
        actions={
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => nav('/admin/plans/new')}
          >
            <Icon name="plus" size={13} />
            Новый план
          </button>
        }
      />
      <div style={{ padding: '24px 28px', flex: 1, overflowY: 'auto' }}>
        {err && (
          <div
            className="card"
            style={{
              padding: 14,
              marginBottom: 14,
              color: 'var(--danger-fg)',
              background: 'var(--danger-subtle-bg)',
            }}
          >
            {err}
          </div>
        )}
        <div className="card">
          <div style={{ padding: '0 20px' }}>
            <div className="tabs">
              <button
                type="button"
                className={'tab ' + (tab === 'active' ? 'active' : '')}
                onClick={() => setTab('active')}
              >
                Активные {items ? `(${counts.active})` : ''}
              </button>
              <button
                type="button"
                className={'tab ' + (tab === 'inactive' ? 'active' : '')}
                onClick={() => setTab('inactive')}
              >
                Выключенные {items ? `(${counts.inactive})` : ''}
              </button>
            </div>
          </div>
          <div className="card-header" style={{ gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
              <div style={{ position: 'relative', maxWidth: 320, flex: 1 }}>
                <div
                  style={{
                    position: 'absolute',
                    left: 10,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--fg-subtle)',
                  }}
                >
                  <Icon name="search" size={14} />
                </div>
                <input
                  className="input"
                  placeholder="Искать по имени или URL…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  style={{ paddingLeft: 32 }}
                />
              </div>
            </div>
            <div style={{ fontSize: 13, color: 'var(--fg-subtle)' }}>
              {items
                ? `${filtered.length} из ${tab === 'active' ? counts.active : counts.inactive}`
                : '…'}
            </div>
          </div>
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 74 }}></th>
                <th>План</th>
                <th>URL</th>
                <th style={{ textAlign: 'right' }}>₪/мес</th>
                <th>Создан</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} onClick={() => nav(`/admin/plans/${p.id}`)}>
                  <td>
                    <span
                      className={'chip no-dot ' + (p.isActive ? 'chip-ok' : 'chip-neutral')}
                      style={{ fontSize: 10.5, height: 18, padding: '0 6px' }}
                    >
                      {p.isActive ? 'on' : 'off'}
                    </span>
                  </td>
                  <td>
                    <div style={{ fontWeight: 500 }}><bdi>{p.name}</bdi></div>
                    {p.shortUrl && (
                      <div
                        style={{
                          fontSize: 11.5,
                          color: 'var(--fg-subtle)',
                          fontFamily: 'var(--font-mono)',
                          marginTop: 2,
                        }}
                      >
                        {p.shortUrl.replace('https://', '')}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className="mono subtle" style={{ fontSize: 12.5 }}>
                      {p.slug}
                    </span>
                  </td>
                  <td className="num" style={{ textAlign: 'right' }}>
                    ₪ {fmtAmountPlain(p.priceIls)}
                  </td>
                  <td>
                    <span className="subtle" style={{ fontSize: 12 }}>
                      {fmtDate(p.createdAt)}
                    </span>
                  </td>
                </tr>
              ))}
              {items && filtered.length === 0 && (
                <tr className="no-hover">
                  <td colSpan={5} style={{ textAlign: 'center', color: 'var(--fg-subtle)', padding: 32 }}>
                    {items.length === 0 ? (
                      <>
                        Ни одного плана нет.{' '}
                        <Link to="/admin/plans/new" style={{ textDecoration: 'underline' }}>
                          Создать первый
                        </Link>
                      </>
                    ) : q ? (
                      'Ничего не найдено'
                    ) : tab === 'active' ? (
                      'Нет активных планов'
                    ) : (
                      'Нет выключенных планов'
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
